/**
 * Accounts-feature-scoped lookup cache.
 *
 * Dropdown data (licenses, providers, cost centers, applications,
 * templatify accounts) are read-only during a wizard session. Fetching
 * them on every step mount meant:
 *   - React 18 StrictMode double-invoked effects → 2 requests per step
 *   - Back / Next navigation re-mounted steps → another fetch each time
 *
 * The cache stores the first in-flight promise per key and hands the
 * same promise to every caller. Failures evict the entry so a retry is
 * possible. Call `clearAccountLookupCache()` to force a fresh fetch
 * next time (e.g. on wizard unmount, if you want a new Create/Edit
 * session to see server-side changes).
 */

import { useEffect, useState } from 'react';
import { subscribeResourceMutated } from '@/utils/resourceEvents';
import {
  getAllCostCenters,
  getAllLicenses,
  getAllNotifyApplications,
  getAllProviders,
  getAllTemplatifyApplications,
  getTemplatifyAccounts,
  getTemplatifyGroups,
  getScopeCatalog,
  type CostCenterOption,
  type LicenseOption,
  type ProviderOption,
  type TemplatifyGroupOption,
} from '../services/accountService';
import type {
  ApplicationRowOption,
  TemplatifyExistingAccount,
} from '../types/account';
import type { ScopeCatalogEntry } from '../../user-management/types/user';

type LookupMap = {
  licenses: LicenseOption[];
  providers: ProviderOption[];
  costCenters: CostCenterOption[];
  notifyApps: ApplicationRowOption[];
  templatifyApps: ApplicationRowOption[];
  templatifyAccounts: TemplatifyExistingAccount[];
  templatifyGroups: TemplatifyGroupOption[];
  /** El catalogo de permisos, servido por el backend para que nadie lo copie. */
  scopeCatalog: ScopeCatalogEntry[];
};

export type LookupKey = keyof LookupMap;

const fetchers: { [K in LookupKey]: () => Promise<LookupMap[K]> } = {
  licenses: getAllLicenses,
  providers: getAllProviders,
  costCenters: getAllCostCenters,
  notifyApps: getAllNotifyApplications,
  templatifyApps: getAllTemplatifyApplications,
  templatifyAccounts: getTemplatifyAccounts,
  templatifyGroups: getTemplatifyGroups,
  scopeCatalog: getScopeCatalog,
};

const cache: { [K in LookupKey]?: Promise<LookupMap[K]> } = {};

const ensure = <K extends LookupKey>(key: K): Promise<LookupMap[K]> => {
  const hit = cache[key] as Promise<LookupMap[K]> | undefined;
  if (hit) return hit;
  const p = fetchers[key]().catch((err) => {
    delete cache[key];
    throw err;
  });
  (cache as Record<LookupKey, Promise<unknown>>)[key] = p;
  return p;
};

const RESOURCE_TO_LOOKUP_KEY: Record<string, LookupKey> = {
  providers: 'providers',
  licenses: 'licenses',
  applications: 'notifyApps',
  'credit-consumption/cost-center': 'costCenters',
};

subscribeResourceMutated((resource) => {
  const key = RESOURCE_TO_LOOKUP_KEY[resource];
  if (key) delete cache[key];
});

export const getAccountLookup = <K extends LookupKey>(
  key: K
): Promise<LookupMap[K]> => ensure(key);

export const clearAccountLookupCache = (): void => {
  (Object.keys(cache) as LookupKey[]).forEach((k) => delete cache[k]);
};

export interface UseAccountLookupResult<K extends LookupKey> {
  data: LookupMap[K] | undefined;
  loading: boolean;
}

export const useAccountLookup = <K extends LookupKey>(
  key: K
): UseAccountLookupResult<K> => {
  const [data, setData] = useState<LookupMap[K] | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    ensure(key)
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return { data, loading };
};
